import { createContext, ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline, Theme } from '@mui/material';
import { SettingsApiModel } from '../../../types/models/settings.model';
import { useGetSettings } from '../hooks/api/use-settings.hook';
import { LoadingIndicator } from '../components/LoadingIndicator';

export interface CustomTheme extends SettingsApiModel, Theme {
  companyName: NonNullable<SettingsApiModel['companyName']>;
  companyLogo: NonNullable<SettingsApiModel['companyLogo']>;
}

const defaultSettings: SettingsApiModel = {
  companyLogo: '/CALMS_Logo_DB_WH_SUB.png',
  companyName: 'Digital Products Catalog',
};

type Props = {
  children: ReactNode;
};

export const ThemeContext = createContext<SettingsApiModel | null>(null);

export const ThemeProvider = ({ children }: Props) => {
  const { settings, loading } = useGetSettings();

  const finalSettings = { ...defaultSettings };
  if (settings) {
    const validSettings = Object.entries(settings).reduce((acc, [key, value]) => {
      if (value === null) return acc;
      if (key === 'companyLogo') return { ...acc, [key]: `${import.meta.env.VITE_CLOUDFRONT_URL}${value}` };
      return { ...acc, [key]: value };
    }, {});
    Object.assign(finalSettings, validSettings);
  }

  const theme = createTheme({
    palette: {
      primary: {
        light: '#2095c9',
        main: '#046B99',
        dark: '#094662',
      },
      secondary: {
        light: '#777777',
        main: '#333333',
        dark: '#1a1a1a',
      },
    },
    typography: {
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        color: '#000',
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        color: '#999',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: ({ palette }) => ({
          '::selection': {
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
          },
        }),
      },
    },
    ...finalSettings,
  });

  return (
    <>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <ThemeContext.Provider value={finalSettings}>{loading ? <LoadingIndicator /> : children}</ThemeContext.Provider>
      </MuiThemeProvider>
    </>
  );
};
